import type { CSSProperties, ReactNode } from "react";
import { cx } from "./fieldContext";

export type SegmentedFilterOption<T extends string> = {
  value: T;
  label: ReactNode;
  /** Symbol vor der Beschriftung. */
  icon?: ReactNode;
  /** Kleiner Punkt am Eintrag, z. B. „Sprache ist befüllt“. */
  marked?: boolean;
  /** Semantische Farbe für Referenzmarkierung und dezente Auswahlfläche. */
  tone?: "success" | "warning" | "danger";
  title?: string;
};

export interface SegmentedFilterProps<T extends string> {
  options: Array<SegmentedFilterOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Barrierefreier Name der Gruppe. */
  label: string;
  /** Kompakt, z. B. Sprachwahl in einem Editor. */
  size?: "small" | "default";
  /** Markierte Optionen als Punkt (Standard), Rahmen oder Farbstreifen unabhängig von der Auswahl. */
  markStyle?: "dot" | "outline" | "underline";
}

/**
 * Segmentwahl mit gleitendem Anzeiger (Kit). Für wenige, sich ausschließende Sichten oder Werte
 * (z. B. alle / Mitglieder / Interessenten; Sprache; Zeitraum).
 */
export function SegmentedFilter<T extends string>({ options, value, onChange, label, size = "default", markStyle = "dot" }: SegmentedFilterProps<T>) {
  const index = Math.max(0, options.findIndex((option) => option.value === value));
  const style = { "--mq-kit-seg-count": options.length, "--mq-kit-seg-index": index } as CSSProperties;
  return (
    <div className={cx("ck-seg", size === "small" && "ck-seg--sm")} role="tablist" aria-label={label} style={style}
      data-selected-tone={options.find((option) => option.value === value)?.tone}>
      <span className="ck-seg-indicator" aria-hidden="true" />
      {options.map((option) => (
        <button key={option.value} type="button" role="tab" aria-selected={option.value === value} title={option.title}
          aria-description={option.title}
          data-tone={option.tone}
          className={cx(option.value === value && "is-active", option.marked && markStyle === "outline" && "is-marked-outline",
            option.marked && markStyle === "underline" && "is-marked-underline")} onClick={() => onChange(option.value)}>
          {option.icon != null ? <span className="ck-seg-icon" aria-hidden="true">{option.icon}</span> : null}
          <span>{option.label}</span>
          {option.marked && markStyle === "dot" ? <span className="ck-seg-mark" aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}
